import axios from 'axios';
import {
  Veterinarian,
  LabTech,
  User,
  Animal,
  AnimalSpecies,
  PortalVersion,
  Instrument,
  InventoryType,
  Test,
  TestType,
  Treatment,
  Practice,
  PracticeInventoryType,
  InventoryTypeSku,
  Queue,
} from '@parasightsysteminc/companion-react';
import store from '../redux/store';
import { clearCookies } from '../redux/actions/authActions';

// Create your axios with credentials object
const BASE_URL =
  process.env.REACT_APP_SERVER_URL || 'http://localhost:8000/api';
export const WEBSOCKET_BASE_URL =
  process.env.REACT_APP_SERVER_WS_URL || 'ws://localhost:8000';
// TODO: make these env vars?
const axiosWithCredentials = axios.create({
  withCredentials: true,
  baseURL: BASE_URL,
});

// Intercept the success and errors, if needed
axiosWithCredentials.interceptors?.response?.use(
  (response) => {
    return Promise.resolve(
      response?.data != null
        ? response.data === ''
          ? null
          : response.data
        : response,
    );
  },
  (error) => {
    if (error?.response?.status === 401) {
      clearCookies(store.dispatch);
    }
    return Promise.reject(
      error?.response?.data?.message || error?.response?.data || error,
    );
  },
);

/*
 * Account Routes
*/

export function logout() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/account/logout').then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function validateDebugPassword(password) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/account/debug/validate', { password }).then(response => {
      resolve(response);
    }).catch(error => {
      reject(error);
    });
  });
}

export function requestConsultation({ name, email, phoneNumber, comments }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/account/request-consultation', { name, email, phoneNumber, comments }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

/*
 * User Routes
*/

export function updatePassword({ password, newPassword }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/account/change-password', { password, newPassword }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function getSelf() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/account').then((user) => {
      resolve(User.thaw(user));
    }).catch(error => {
      reject(error);
    });
  });
}

export function getPractice() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/practice').then((practice) => {
      resolve(Practice.thaw(practice));
    }).catch(error => {
      reject(error);
    });
  });
}

export function getUser() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/account').then((user) => {
      resolve(User.thaw(user));
    }).catch(error => {
      reject(error);
    });
  });
}

// TODO: Update this function with a real api call
export function updateUser({ id, ...user }) {
  return new Promise(function (resolve) {
    resolve(User.thaw({ id, ...user }));
  });
}

// TODO: Update this function with a real api call
export function updateEmail({ id, email }) {
  return new Promise(function (resolve) {
    resolve(User.thaw({ id, email }));
  });
}

// THIS IS TEMPORARY. DO NOT DEPLOY WHEN IN REAL PRODUCTION *********
export function updateDemoMode({ isDemo }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.patch('/practice/demo', { isDemo }).then((res) => {
      resolve(Practice.thaw(res));
    }).catch(error => {
      reject(error);
    });
  });
}
// THIS IS TEMPORARY. DO NOT DEPLOY WHEN IN REAL PRODUCTION *********

/*
 * Auth Routes
*/

export function createAccount({ token, firstName, lastName, phoneNumber, password }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/auth/register', { token, firstName, lastName, phoneNumber, password }).then((user) => {
      resolve(User.thaw(user));
    }).catch(error => {
      reject(error);
    });
  });
}

export function login({ email, password }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/auth/login', { email, password }).then(user => {
      resolve(User.thaw(user));
    }).catch(error => {
      reject(error);
    });
  });
}

export function forgotPasswordRequest({ email }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/auth/forgot-password', { email }).then((message) => {
      resolve(message);
    }).catch(error => {
      reject(error);
    });
  });
}

export function forgotPasswordConfirm({ token, password }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/auth/forgot-password/confirm', { token, password }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

/*
 * Veterinarian Routes
*/

export function listVeterinarians({ options = {} } = {}) {
  let encodedOptions = encodeURI(JSON.stringify(options));
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/veterinarian', { params: { options: encodedOptions } }).then(({ results, totalCount }) => {
      resolve({ results: Veterinarian.thawList(results), totalCount });
    }).catch(error => {
      reject(error);
    });
  });
}

export function updateVeterinarian({ id, ...veterinarianChanges }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.patch('/veterinarian/' + id, veterinarianChanges).then(veterinarian => {
      resolve(Veterinarian.thaw(veterinarian));
    }).catch(error => {
      reject(error);
    });
  });
}

export function createVeterinarian({ firstName, lastName, email, title, sourceId }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/veterinarian', { firstName, lastName, email, title, sourceId }).then(veterinarian => {
      resolve(Veterinarian.thaw(veterinarian));
    }).catch(error => {
      reject(error);
    });
  });
}

export function getVeterinarian(veterinarianId) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/veterinarian/' + veterinarianId).then(veterinarian => {
      resolve(Veterinarian.thaw(veterinarian));
    }).catch(error => {
      reject(error);
    });
  });
}

export function deactivateVeterinarian(id) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.patch('/veterinarian/' + id, { isActive: false }).then(veterinarian => {
      resolve(LabTech.thaw(veterinarian));
    }).catch(error => {
      reject(error);
    });
  });
}

/*
 * Lab Tech Routes
*/

// only gets active lab techs
export function listLabTechs({ options = {} } = {}) {
  let encodedOptions = encodeURI(JSON.stringify(options));
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/lab-tech', { params: { options: encodedOptions } }).then(({ results, totalCount }) => {
      resolve({ results: LabTech.thawList(results), totalCount });
    }).catch(error => {
      reject(error);
    });
  });
}

export function updateLabTech({ id, ...labtechChanges }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.patch('/lab-tech/' + id, labtechChanges).then(labtech => {
      resolve(LabTech.thaw(labtech));
    }).catch(error => {
      reject(error);
    });
  });
}

export function deactivateLabTech(id) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.patch('/lab-tech/' + id, { isActive: false }).then(labTech => {
      resolve(LabTech.thaw(labTech));
    }).catch(error => {
      reject(error);
    });
  });
}


export function createLabTech({ firstName, lastName, email, title, sourceId }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/lab-tech', { firstName, lastName, email, title, sourceId }).then(labtech => {
      resolve(LabTech.thaw(labtech));
    }).catch(error => {
      reject(error);
    });
  });
}

export function getLabTech(id) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/lab-tech/' + id).then(labtech => {
      resolve(LabTech.thaw(labtech));
    }).catch(error => {
      reject(error);
    });
  });
}

/*
 * Instrument Routes
*/

export function listInstruments({ options = {} } = {}) {
  let encodedOptions = encodeURI(JSON.stringify(options));
  return new Promise(function (resolve, reject) {
    axiosWithCredentials
      .get('/instrument', { params: { options: encodedOptions } })
      .then(({ results, totalCount }) => {
        resolve({ results: Instrument.thawList(results), totalCount });
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export function activateInstrument(code, friendlyName) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/instrument/activate', { code, friendlyName }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function deactivateInstrument(serialNumber) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.patch(`/instrument/${serialNumber}/deactivate`).then(instrument => {
      resolve(Instrument.thaw(instrument));
    }).catch(error => {
      reject(error);
    });
  });
}

export function freeInstrument(serialNumber) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.patch(`/instrument/${serialNumber}/free`).then(instrument => {
      resolve(Instrument.thaw(instrument));
    }).catch(error => {
      reject(error);
    });
  });
}

export function updateInstrument(serialNumber, { friendlyName }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.patch(`/instrument/${serialNumber}`, { friendlyName }).then(instrument => {
      resolve(Instrument.thaw(instrument));
    }).catch(error => {
      reject(error);
    });
  });
}

export function getInstrument(serialNumber) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get(`/instrument/${serialNumber}`).then(instrument => {
      resolve(Instrument.thaw(instrument));
    }).catch(error => {
      reject(error);
    });
  });
}

/*
 * Practice Routes
*/
export function listPracticeInventoryTypes({ options = {} } = {}) {
  let encodedOptions = encodeURI(JSON.stringify(options));
  return new Promise(function (resolve, reject) {
    axiosWithCredentials
      .get('/practice/inventory', { params: { options: encodedOptions } })
      .then(results => {
        resolve(PracticeInventoryType.thawList(results));
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export function updateInventoryTypeCount({ inventoryTypeId, count }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/practice/inventory/update', { inventoryTypeId, count }).then(practiceInventoryTypes => {
      resolve(PracticeInventoryType.thawList(practiceInventoryTypes));
    }).catch(error => {
      reject(error);
    });
  });
}

export function updateOrderTriggerNumberCounts( thresholdArray ) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/practice/inventory/threshold', thresholdArray).then(practiceInventoryTypes => {
      resolve(PracticeInventoryType.thawList(practiceInventoryTypes));
    }).catch(error => {
      reject(error);
    });
  });
}

export function listCartridgeInventoryTypes() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/inventory/type/cartridge').then(({ results }) => {
      resolve(InventoryType.thawList(results));
    }).catch(error => {
      reject(error);
    });
  });
}


/*
 * Animal Routes
*/

export function listAnimals({ options = {} } = {}) {
  let encodedOptions = encodeURI(JSON.stringify(options));
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/animal', { params: { options: encodedOptions } })
      .then(({ results, totalCount }) => {
        resolve({ results: Animal.thawList(results), totalCount });
      }).catch(error => {
        reject(error);
      });
  });
}

export function listAnimalsForAutocomplete(search) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/animal/autocomplete', { params: { search } })
      .then((results) => {
        resolve(Animal.thawList(results));
      }).catch(error => {
        reject(error);
      });
  });

}

export function listAnimalSpecies() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/animal/species')
      .then(({ results }) => {
        resolve(AnimalSpecies.thawList(results));
      }).catch(error => {
        reject(error);
      });
  });
}

export function updateAnimal({ id, name, animalSpeciesId, ownerFirstName, ownerLastName, ownerEmail }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.patch(`/animal/${id}`, { name, animalSpeciesId, ownerFirstName, ownerLastName, ownerEmail: ownerEmail === '' ? null : ownerEmail }).then(animal => {
      resolve(Animal.thaw(animal));
    }).catch(error => {
      reject(error);
    });
  });
}

export function createAnimal({ name, animalSpeciesId, ownerFirstName, ownerLastName, ownerEmail }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/animal', { name, animalSpeciesId, ownerFirstName, ownerLastName, ownerEmail: ownerEmail === '' ? null : ownerEmail }).then(animal => {
      resolve(Animal.thaw(animal));
    }).catch(error => {
      reject(error);
    });
  });
}

export function getAnimal(id) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/animal/' + id).then(animal => {
      resolve(Animal.thaw(animal));
    }).catch(error => {
      reject(error);
    });
  });
}

/*
 * Treatment Routes
*/

export function listTreatments({ animalSpeciesId, options = {} } = {}) {
  let encodedOptions = encodeURI(JSON.stringify(options));
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/treatment', { params: { animalSpeciesId, options: encodedOptions } }).then(({ results, totalCount }) => {
      resolve({ results: Treatment.thawList(results), totalCount });
    }).catch(error => {
      reject(error);
    });
  });
}

export function getTreatment(id) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/treatment/' + id).then(treatment => {
      resolve(Treatment.thaw(treatment));
    }).catch(error => {
      reject(error);
    });
  });
}

export function createTreatment({ name, animalSpeciesId }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/treatment', { name, animalSpeciesId }).then(treatment => {
      resolve(Treatment.thaw(treatment));
    }).catch(error => {
      reject(error);
    });
  });
}

export function deleteTreatment(id) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.patch('/treatment/' + id, { isActive: false }).then(treatment => {
      resolve(Treatment.thaw(treatment));
    }).catch(error => {
      reject(error);
    });
  });
}

/*
 * Test Routes
*/

export function createTest({ veterinarianId, labTechId, animalId, testTypeId, instrumentSerialNumber, animalAge, sampleName, comments }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/test', { veterinarianId, labTechId, animalId, testTypeId, instrumentSerialNumber, animalAge, sampleName, comments }).then(test => {
      resolve(Test.thaw(test));
    }).catch(error => {
      reject(error);
    });
  });
}

export function createTestFromQueue({ queueId, instrumentSerialNumber }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/test', { queueId, instrumentSerialNumber }).then(test => {
      resolve(Test.thaw(test));
    }).catch(error => {
      reject(error);
    });
  });
}

export function createEllieTest({ labdaqAccessionId, sampleQualityDescriptor, instrumentSerialNumber }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/test/ellie', { labdaqAccessionId, sampleQualityDescriptor, instrumentSerialNumber }).then(test => {
      resolve(Test.thaw(test));
    }).catch(error => {
      reject(error);
    });
  });
}

export function submitTestRecommendations(id, { treatmentIds, notes, sendResultsToOwner }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.patch(`/test/${id}`, { treatmentIds, notes, sendResultsToOwner }).then(res => {
      resolve({ ...res, test: Test.thaw(res.test) });
    }).catch(error => {
      reject(error);
    });
  });
}

export function getTest(id) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get(`/test/${id}`).then(([test, pdfResultsUrl]) => {
      test.pdfResultsUrl = pdfResultsUrl;
      resolve(Test.thaw(test));
    }).catch(error => {
      reject(error);
    });
  });
}

export function listQueuedTests() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/test/queue').then((results) => {
      resolve(Queue.thawList(results));
    }).catch(error => {
      reject(error);
    });
  });
}

export function listTests({ options = {} } = {}) {
  let encodedOptions = encodeURI(JSON.stringify(options));
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/test', { params: { options: encodedOptions } }).then(({ results, totalCount }) => {
      resolve({ results: Test.thawList(results), totalCount });
    }).catch(error => {
      reject(error);
    });
  });
}

export function listTestTypes({ options = {} } = {}) {
  let encodedOptions = encodeURI(JSON.stringify(options));
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/test/type', { params: { options: encodedOptions } }).then(({ results, totalCount }) => {
      resolve({ results: TestType.thawList(results), totalCount });
    }).catch(error => {
      reject(error);
    });
  });
}

export function countTests(startCountDate, endCountDate) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/practice/', { params: { includeCounts: true, startCountDate, endCountDate } }).then(practice => {
      resolve(Practice.thaw(practice));
    }).catch(error => {
      reject(error);
    });
  });
}

/*
 * InventoryType Routes
*/

export function listInventoryTypeSkus() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/inventory/type/sku').then((results) => {
      resolve(InventoryTypeSku.thawList(results));
    }).catch(error => {
      reject(error);
    });
  });
}

export function getTestTypeFromSku(sku) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get(`/test/type/sku/${sku}`).then(testType => {
      resolve(TestType.thaw(testType));
    }).catch(error => {
      reject(error);
    });
  });
}

/*
  * Instrument Data Routes
 */

export function getIsDualEggChamber(instrumentSerial) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get(`/instrument/${instrumentSerial}/dual`)
      .then(response => {
        resolve(response);
      }).catch(error => {
        reject(error);
      });
  });
}

// For testing only
// export function getIsDualEggChamber() {
//   return new Promise(function (resolve) {
//     resolve(true);
//   });
// }

export function getSensorData(instrumentSerial) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get(`/instrument/${instrumentSerial}/sensors`)
      .then(response => {
        resolve(response);
      }).catch(error => {
        reject(error);
      });
  });
}

// For testing only
// export function getSensorData() {
//   return new Promise(function (resolve) {
//     const FAKE_RETURNED_SENSOR_VALUES = { 'fluidSensor1': [0, 0, 0, 0, 0], 'fluidSensor2': [0, 0, 0, 0, 0], 'fluidSensor3': [0, 0, 0, 0, 0], 'fluidSensor4': [0, 0, 0, 0, 0], 'fluidSensor5': [0, 0, 0, 0, 0], 'fluidSensor6': [0, 0, 0, 0, 0], 'dropSensor1': [3, 0, 4, 3, 3], 'dropSensor2': [0, 0, 0, 0, 0], 'homeSwitch': [false, false, false, false, false], 'latchSwitch': [true, true, true, true, true], 'eggChamberSwitch': [true, true, true, true, true], 'fluidDrawerSwitch': [false, false, false, false, false], 'fluidMotorFault1': [false, false, false, false, false], 'fluidMotorFault2': [false, false, false, false, false], 'fluidMotorFault3': [false, false, false, false, false], 'fluidMotorFault4': [false, false, false, false, false], 'fluidMotorFault5': [false, false, false, false, false], 'fluidMotorFault6': [false, false, false, false, false], 'shuttleMotorFault': [false, false, false, false, false], 'watchdogFault': [false, false, false, false, false], 'pressureSensor': [12, 9, 11, 11, 11], 'linearEncoder': [132414, 132414, 132414, 132414, 132414], 'linearEncoderErrorBit': [false, false, false, false, false], 'linearEncoderReadStatus': [true, true, true, true, true], 'cartridgePresent1': [0, 0, 0, 0, 0], 'cartridgePresent2': [0, 0, 0, 0, 0], 'cartridgePresent3': [0, 0, 0, 0, 0], 'cartridgePresent4': [0, 0, 0, 0, 0], 'cartridgePresent5': [0, 0, 0, 0, 0], 'cartridgePresent6': [0, 0, 0, 0, 0], 'focusMotorFault': [false, false, false, false, false], 'focusHomeSwitch': [false, false, false, false, false], 'cartridgeReady1': [true, true, true, true, true], 'cartridgeReady2': [true, true, true, true, true], 'fluidLine1': ['FLUID_PRESENT', 'FLUID_PRESENT', 'FLUID_PRESENT', 'FLUID_PRESENT', 'FLUID_PRESENT'], 'fluidLine2': ['FLUID_PRESENT', 'FLUID_PRESENT', 'FLUID_PRESENT', 'FLUID_PRESENT', 'FLUID_PRESENT'], 'fluidLine3': ['FLUID_PRESENT', 'FLUID_PRESENT', 'FLUID_PRESENT', 'FLUID_PRESENT', 'FLUID_PRESENT'], 'fluidLine4': ['FLUID_PRESENT', 'FLUID_PRESENT', 'FLUID_PRESENT', 'FLUID_PRESENT', 'FLUID_PRESENT'], 'fluidLine5': ['FLUID_PRESENT', 'FLUID_PRESENT', 'FLUID_PRESENT', 'FLUID_PRESENT', 'FLUID_PRESENT'], 'cartridgeFluidLevel1': ['CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD'], 'cartridgeFluidLevel2': ['CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD'], 'cartridgeFluidLevel3': ['CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD'], 'cartridgeFluidLevel4': ['CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD'], 'cartridgeFluidLevel5': ['CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD', 'CARTRIDGE_FLUID_LEVEL_GOOD'] };
//     resolve(FAKE_RETURNED_SENSOR_VALUES);
//   });
// }

/*
 * Portal Version
 */
export function getNewestPortalVersion() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/portal/newest').then((res) => {
      resolve(PortalVersion.thaw(res));
    }).catch(error => {
      reject(error);
    });
  });
}

/*
 * Platform Settings
 */
export function getUnderConstruction() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/platform-setting/under-construction').then((res) => {
      resolve(res);
    }).catch(error => {
      reject(error);
    });
  });
}

export function getInfoBanner() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/platform-setting/info-banner').then((res) => {
      resolve(res);
    }).catch(error => {
      reject(error);
    });
  });
}

/**
 * @returns {Promise<import('../../../companion-portal-server/routes/practice/pims').PimsIntegrationStatus | null>}
 */
export async function getPracticePimsStatus() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/practice/pims/status').then((res) => {
      resolve(res.data);
    }).catch(error => {
      reject(error);
    });
  });
}

export async function listIntegrationEmployees() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/practice/pims/employee').then((res) => {
      resolve(res);
    }).catch(error => {
      reject(error);
    });
  });
}

/**
 * @param {object} query
 * @param {string} [query.firstName]
 * @param {string} [query.lastName]
 * @param {string} [query.email]
 * @param {string} [query.phone]
 * @returns {Promise<{ clients:import('../../../companion-portal-server/pims/PimsService').PimsClient[], truncated:boolean }>}
 */
export function checkClients(query) {
  return axiosWithCredentials.get('/practice/pims/check-clients', { params: query });
}

/**
 * @param {object} query
 * @param {string} query.clientId
 * @returns {Promise<{ animals:import('@parasightsysteminc/companion-react').Animal[], truncated:boolean }>}
 */
export function getClientAnimals(query) {
  return axiosWithCredentials
    .get('/practice/pims/client-animals', { params: query })
    .then((data) => {
      return {
        animals: Animal.thawList(data.animals),
        truncated: data.truncated,
      };
    });
}

export function sendTestToPims(testId) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post(`/practice/pims/test/${testId}`).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}